import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  Background,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import { API, getAuthHeader } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Plus, Save, Trash2, Square, Circle, Diamond, Hexagon,
  RectangleHorizontal, ChevronDown, Palette, Type, GitBranch,
  FilePlus, FolderOpen, Pencil, Check, X, Undo2, FileText
} from 'lucide-react';
import NodeNotesSidePanel from './NodeNotesSidePanel';

/* ─── Shape & Color Config ─── */
const SHAPES = [
  { id: 'rectangle', label: 'Rectangle', icon: Square },
  { id: 'rounded', label: 'Rounded', icon: RectangleHorizontal },
  { id: 'circle', label: 'Circle', icon: Circle },
  { id: 'diamond', label: 'Diamond', icon: Diamond },
  { id: 'hexagon', label: 'Hexagon', icon: Hexagon },
];

const COLORS = [
  { id: 'blue', bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  { id: 'green', bg: '#dcfce7', border: '#22c55e', text: '#166534' },
  { id: 'amber', bg: '#fef9c4', border: '#eab308', text: '#854d0e' },
  { id: 'rose', bg: '#ffe4e6', border: '#f43f5e', text: '#9f1239' },
  { id: 'violet', bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' },
  { id: 'slate', bg: '#f1f5f9', border: '#64748b', text: '#1e293b' },
  { id: 'cyan', bg: '#cffafe', border: '#06b6d4', text: '#155e75' },
  { id: 'orange', bg: '#ffedd5', border: '#f97316', text: '#9a3412' },
];

const getShapeStyle = (shape, color) => {
  const c = COLORS.find(cl => cl.id === color) || COLORS[0];
  const base = {
    background: c.bg,
    borderColor: c.border,
    color: c.text,
    borderWidth: 2,
    borderStyle: 'solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  };
  switch (shape) {
    case 'rounded':
      return { ...base, borderRadius: 16, minWidth: 140, minHeight: 52, padding: '8px 20px' };
    case 'circle':
      return { ...base, borderRadius: '50%', width: 110, height: 110, padding: 12 };
    case 'diamond':
      return { ...base, borderRadius: 4, width: 100, height: 100, transform: 'rotate(45deg)', padding: 8 };
    case 'hexagon':
      return { ...base, clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)', minWidth: 130, minHeight: 90, padding: '12px 28px' };
    default: // rectangle
      return { ...base, borderRadius: 6, minWidth: 140, minHeight: 52, padding: '8px 20px' };
  }
};

/* ─── Custom Node ─── */
const CustomNode = ({ id, data, selected }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(data.label);
  const inputRef = useRef(null);
  const hoverTimer = useRef(null);
  const { setNodes } = useReactFlow();

  useEffect(() => { setText(data.label); }, [data.label]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitEdit = useCallback(() => {
    const val = text.trim() || 'New Node';
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, label: val } } : n));
    setEditing(false);
  }, [id, text, setNodes]);

  const cancelEdit = useCallback(() => {
    setText(data.label);
    setEditing(false);
  }, [data.label]);

  // Dispatch open-notes event (side panel will catch it)
  const dispatchOpen = useCallback(() => {
    if (!data.mapId) return;
    window.dispatchEvent(new CustomEvent('mindmap:open-notes', {
      detail: { mapId: data.mapId, nodeId: id, nodeLabel: data.label },
    }));
  }, [data.mapId, id, data.label]);

  // Click: open immediately
  const openNotes = useCallback((e) => {
    e.stopPropagation();
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    dispatchOpen();
  }, [dispatchOpen]);

  // Hover: open after 700ms delay
  const handleFileHover = useCallback(() => {
    if (!data.mapId) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => { dispatchOpen(); }, 700);
  }, [data.mapId, dispatchOpen]);

  const handleFileLeave = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  const shape = data.shape || 'rectangle';
  const color = data.color || 'blue';
  const style = getShapeStyle(shape, color);
  const isDiamond = shape === 'diamond';

  return (
    <div
      style={{ ...style, boxShadow: selected ? `0 0 0 2px ${style.borderColor}` : '0 1px 3px rgba(0,0,0,0.08)', position: 'relative' }}
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      data-testid={`mindmap-node-${id}`}
    >
      <Handle type="target" position={Position.Top} style={{ background: style.borderColor, width: 8, height: 8, border: 'none' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: style.borderColor, width: 8, height: 8, border: 'none' }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ background: style.borderColor, width: 8, height: 8, border: 'none' }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ background: style.borderColor, width: 8, height: 8, border: 'none' }} />

      {/* File icon for notes */}
      {!isDiamond && (
        <div
          className="absolute -top-1 -right-1 z-10"
          style={isDiamond ? { transform: 'rotate(-45deg)' } : {}}
        >
          <button
            onClick={openNotes}
            onMouseEnter={handleFileHover}
            onMouseLeave={handleFileLeave}
            className="w-5 h-5 rounded-full flex items-center justify-center transition-all hover:scale-125"
            style={{
              background: style.borderColor,
              color: 'white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
            data-testid={`node-notes-icon-${id}`}
            title="Hover or click to open notes"
          >
            <FileText className="w-3 h-3" />
          </button>
        </div>
      )}

      <div style={isDiamond ? { transform: 'rotate(-45deg)', textAlign: 'center', fontSize: 12, lineHeight: 1.2 } : { textAlign: 'center', fontSize: 13, lineHeight: 1.3, fontWeight: 500 }}>
        {editing ? (
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
            className="bg-transparent border-none outline-none text-center w-full"
            style={{ color: style.color, fontSize: 'inherit', fontWeight: 'inherit', minWidth: 60 }}
            data-testid="node-text-input"
          />
        ) : (
          <span className="cursor-text select-none">{data.label}</span>
        )}
      </div>

      {/* For diamond nodes: file icon below */}
      {isDiamond && (
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10" style={{ transform: 'rotate(-45deg) translateX(-50%)' }}>
          <button
            onClick={openNotes}
            onMouseEnter={handleFileHover}
            onMouseLeave={handleFileLeave}
            className="w-5 h-5 rounded-full flex items-center justify-center transition-all hover:scale-125"
            style={{ background: style.borderColor, color: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
            data-testid={`node-notes-icon-${id}`}
            title="Hover or click to open notes"
          >
            <FileText className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Node Toolbar (appears when node selected) ─── */
const NodeToolbar = ({ selectedNode, onChangeShape, onChangeColor, onDelete }) => {
  const [showShapes, setShowShapes] = useState(false);
  const [showColors, setShowColors] = useState(false);

  if (!selectedNode) return null;

  const currentShape = selectedNode.data?.shape || 'rectangle';
  const currentColor = selectedNode.data?.color || 'blue';

  return (
    <div
      className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-white rounded-xl shadow-lg border border-slate-200 px-2 py-1.5"
      data-testid="node-toolbar"
    >
      {/* Shape picker */}
      <div className="relative">
        <button
          onClick={() => { setShowShapes(!showShapes); setShowColors(false); }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-sm text-slate-700 font-medium transition-colors"
          data-testid="shape-picker-btn"
        >
          {(() => { const S = SHAPES.find(s => s.id === currentShape); return S ? <S.icon className="w-4 h-4" /> : null; })()}
          <span className="hidden sm:inline">Shape</span>
          <ChevronDown className="w-3 h-3" />
        </button>
        {showShapes && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[150px]">
            {SHAPES.map(s => (
              <button
                key={s.id}
                onClick={() => { onChangeShape(s.id); setShowShapes(false); }}
                data-testid={`shape-option-${s.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                  currentShape === s.id ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-slate-700'
                }`}
              >
                <s.icon className="w-4 h-4" />
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-slate-200" />

      {/* Color picker */}
      <div className="relative">
        <button
          onClick={() => { setShowColors(!showColors); setShowShapes(false); }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-sm text-slate-700 font-medium transition-colors"
          data-testid="color-picker-btn"
        >
          <div className="w-4 h-4 rounded-full border-2" style={{
            backgroundColor: (COLORS.find(c => c.id === currentColor) || COLORS[0]).bg,
            borderColor: (COLORS.find(c => c.id === currentColor) || COLORS[0]).border,
          }} />
          <span className="hidden sm:inline">Color</span>
          <ChevronDown className="w-3 h-3" />
        </button>
        {showColors && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-2 grid grid-cols-4 gap-1.5 min-w-[120px]">
            {COLORS.map(c => (
              <button
                key={c.id}
                onClick={() => { onChangeColor(c.id); setShowColors(false); }}
                data-testid={`color-option-${c.id}`}
                className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                  currentColor === c.id ? 'ring-2 ring-offset-1 ring-blue-400 scale-110' : ''
                }`}
                style={{ backgroundColor: c.bg, borderColor: c.border }}
                title={c.id}
              />
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-slate-200" />

      {/* Delete node */}
      <button
        onClick={onDelete}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-red-50 text-sm text-red-500 font-medium transition-colors"
        data-testid="delete-node-btn"
      >
        <Trash2 className="w-4 h-4" />
        <span className="hidden sm:inline">Delete</span>
      </button>
    </div>
  );
};

/* ─── Default initial nodes for a new map ─── */
const defaultNodes = [
  { id: '1', type: 'custom', position: { x: 300, y: 50 }, data: { label: 'Central Idea', shape: 'rounded', color: 'blue' } },
  { id: '2', type: 'custom', position: { x: 100, y: 200 }, data: { label: 'Branch A', shape: 'rectangle', color: 'green' } },
  { id: '3', type: 'custom', position: { x: 500, y: 200 }, data: { label: 'Branch B', shape: 'rectangle', color: 'amber' } },
];
const defaultEdges = [
  { id: 'e1-2', source: '1', target: '2', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#94a3b8', strokeWidth: 2 } },
  { id: 'e1-3', source: '1', target: '3', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#94a3b8', strokeWidth: 2 } },
];

/* ─── Mind Map Inner (needs ReactFlowProvider) ─── */
const MindMapInner = ({ user }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [maps, setMaps] = useState([]);
  const [activeMapId, setActiveMapId] = useState(null);
  const [mapName, setMapName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMapList, setShowMapList] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [notesPanel, setNotesPanel] = useState({ open: false, mapId: null, nodeId: null, nodeLabel: '' });
  const nameInputRef = useRef(null);
  const nodeIdCounter = useRef(10);
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition } = useReactFlow();

  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);

  // Track changes
  useEffect(() => { if (!loading) setHasUnsaved(true); }, [nodes, edges]);

  // Fetch mind maps
  const fetchMaps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mindmaps`, { headers: getAuthHeader() });
      setMaps(res.data);
      if (res.data.length > 0 && !activeMapId) {
        loadMap(res.data[0]);
      } else if (res.data.length === 0) {
        createNewMap();
      }
    } catch {
      toast.error('Failed to load mind maps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMaps(); }, []);

  // Listen for "open notes" events dispatched from CustomNode file icon
  useEffect(() => {
    const handler = (e) => {
      const { mapId: nMapId, nodeId, nodeLabel } = e.detail || {};
      if (!nMapId || !nodeId) return;
      setNotesPanel({ open: true, mapId: nMapId, nodeId, nodeLabel: nodeLabel || '' });
    };
    window.addEventListener('mindmap:open-notes', handler);
    return () => window.removeEventListener('mindmap:open-notes', handler);
  }, []);

  const closeNotesPanel = useCallback(() => {
    setNotesPanel((p) => ({ ...p, open: false }));
  }, []);

  const loadMap = (map) => {
    setActiveMapId(map.id);
    setMapName(map.name);
    // Inject mapId into each node's data for notes link
    const nodesWithMapId = (map.nodes || []).map(n => ({
      ...n,
      data: { ...n.data, mapId: map.id },
    }));
    setNodes(nodesWithMapId);
    setEdges(map.edges || []);
    setSelectedNodeId(null);
    setHasUnsaved(false);
    setShowMapList(false);
    const maxId = Math.max(0, ...(map.nodes || []).map(n => parseInt(n.id) || 0));
    nodeIdCounter.current = maxId + 1;
  };

  const createNewMap = () => {
    setActiveMapId(null);
    setMapName('Untitled Mind Map');
    setNodes(defaultNodes);
    setEdges(defaultEdges);
    setSelectedNodeId(null);
    setHasUnsaved(true);
    setShowMapList(false);
    nodeIdCounter.current = 10;
  };

  const saveMap = async () => {
    setSaving(true);
    try {
      // Strip mapId from node data before saving (UI-only field)
      const cleanNodes = nodes.map(n => ({
        ...n,
        data: { ...n.data, mapId: undefined },
      }));
      const payload = { name: mapName || 'Untitled', nodes: cleanNodes, edges };
      if (activeMapId) {
        await axios.put(`${API}/mindmaps/${activeMapId}`, payload, { headers: getAuthHeader() });
        toast.success('Mind map saved');
      } else {
        const res = await axios.post(`${API}/mindmaps`, payload, { headers: getAuthHeader() });
        const newId = res.data.id;
        setActiveMapId(newId);
        // Update all nodes with new mapId
        setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, mapId: newId } })));
        toast.success('Mind map created');
      }
      setHasUnsaved(false);
      fetchMaps();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteMap = async () => {
    if (!activeMapId) return;
    if (!window.confirm('Delete this mind map?')) return;
    try {
      await axios.delete(`${API}/mindmaps/${activeMapId}`, { headers: getAuthHeader() });
      toast.success('Mind map deleted');
      setActiveMapId(null);
      fetchMaps();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // Node operations
  const onConnect = useCallback((params) => {
    setEdges(eds => addEdge({
      ...params,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { stroke: '#94a3b8', strokeWidth: 2 },
    }, eds));
  }, [setEdges]);

  const addNode = useCallback(() => {
    const id = String(nodeIdCounter.current++);
    const newNode = {
      id,
      type: 'custom',
      position: { x: 200 + Math.random() * 200, y: 150 + Math.random() * 200 },
      data: { label: 'New Node', shape: 'rectangle', color: 'slate', mapId: activeMapId },
    };
    setNodes(nds => [...nds, newNode]);
  }, [setNodes, activeMapId]);

  const onSelectionChange = useCallback(({ nodes: selNodes }) => {
    setSelectedNodeId(selNodes.length === 1 ? selNodes[0].id : null);
  }, []);

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  const changeNodeShape = useCallback((shape) => {
    if (!selectedNodeId) return;
    setNodes(nds => nds.map(n => n.id === selectedNodeId ? { ...n, data: { ...n.data, shape } } : n));
  }, [selectedNodeId, setNodes]);

  const changeNodeColor = useCallback((color) => {
    if (!selectedNodeId) return;
    setNodes(nds => nds.map(n => n.id === selectedNodeId ? { ...n, data: { ...n.data, color } } : n));
  }, [selectedNodeId, setNodes]);

  const deleteSelectedNode = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes(nds => nds.filter(n => n.id !== selectedNodeId));
    setEdges(eds => eds.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  }, [selectedNodeId, setNodes, setEdges]);

  const onPaneClick = useCallback(() => { setSelectedNodeId(null); }, []);

  // Double-click on pane to add node at cursor
  const onPaneDoubleClick = useCallback((event) => {
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const id = String(nodeIdCounter.current++);
    setNodes(nds => [...nds, {
      id,
      type: 'custom',
      position,
      data: { label: 'New Node', shape: 'rectangle', color: 'slate', mapId: activeMapId },
    }]);
  }, [screenToFlowPosition, setNodes, activeMapId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-140px)]">
        <div className="text-slate-500">Loading mind maps...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]" data-testid="mindmap-panel">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-2.5 mb-3 flex-wrap">
        {/* Left: Map selector + Name */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative">
            <button
              onClick={() => setShowMapList(!showMapList)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition-colors"
              data-testid="map-selector-btn"
            >
              <FolderOpen className="w-4 h-4" />
              <ChevronDown className="w-3 h-3" />
            </button>
            {showMapList && (
              <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[220px] z-30 max-h-[300px] overflow-y-auto">
                <button
                  onClick={createNewMap}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 font-medium"
                  data-testid="new-map-btn-dropdown"
                >
                  <FilePlus className="w-4 h-4" />
                  New Mind Map
                </button>
                <div className="border-t border-slate-100 my-1" />
                {maps.map(m => (
                  <button
                    key={m.id}
                    onClick={() => loadMap(m)}
                    data-testid={`map-item-${m.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                      activeMapId === m.id ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-slate-700'
                    }`}
                  >
                    <GitBranch className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{m.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Map name (editable) */}
          {editingName ? (
            <div className="flex items-center gap-1">
              <input
                ref={nameInputRef}
                value={mapName}
                onChange={e => setMapName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') setEditingName(false); if (e.key === 'Escape') setEditingName(false); }}
                className="text-base font-semibold text-slate-900 bg-slate-100 border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-400 min-w-[150px]"
                data-testid="map-name-input"
                autoFocus
              />
              <button onClick={() => setEditingName(false)} className="p-1 rounded hover:bg-slate-100 text-green-600">
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditingName(true)}
              className="flex items-center gap-1.5 text-base font-semibold text-slate-900 hover:text-blue-600 transition-colors group"
              data-testid="map-name-display"
            >
              <span className="truncate max-w-[200px]">{mapName}</span>
              <Pencil className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}

          {hasUnsaved && <span className="text-xs text-amber-500 font-medium">unsaved</span>}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={addNode}
            className="text-slate-700"
            data-testid="add-node-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Node
          </Button>
          <Button
            size="sm"
            onClick={saveMap}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="save-map-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {activeMapId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={deleteMap}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid="delete-map-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 relative overflow-hidden" ref={reactFlowWrapper}>
        <NodeToolbar
          selectedNode={selectedNode}
          onChangeShape={changeNodeShape}
          onChangeColor={changeNodeColor}
          onDelete={deleteSelectedNode}
        />
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={onSelectionChange}
          onPaneClick={onPaneClick}
          onPaneDoubleClick={onPaneDoubleClick}
          nodeTypes={nodeTypes}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
          multiSelectionKeyCode="Shift"
          snapToGrid
          snapGrid={[15, 15]}
          defaultEdgeOptions={{
            type: 'smoothstep',
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: '#94a3b8', strokeWidth: 2 },
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls
            position="bottom-left"
            showInteractive={false}
            className="!bg-white !shadow-md !border !border-slate-200 !rounded-lg"
          />
          <MiniMap
            position="bottom-right"
            nodeColor={(n) => {
              const c = COLORS.find(cl => cl.id === (n.data?.color || 'blue'));
              return c ? c.border : '#94a3b8';
            }}
            className="!bg-slate-50 !border !border-slate-200 !rounded-lg !shadow-md"
            maskColor="rgba(241,245,249,0.7)"
          />
          <Background variant="dots" gap={20} size={1} color="#e2e8f0" />
        </ReactFlow>

        {/* Hint */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-slate-400">
              <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">Double-click to add a node</p>
              <p className="text-sm mt-1">Or use the "Add Node" button above</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 px-1">
        <span>Double-click node to edit text</span>
        <span>Drag between handles to connect</span>
        <span>Select node to change shape/color</span>
        <span>Delete/Backspace to remove selected</span>
      </div>

      {/* Right-side Notes Panel */}
      <NodeNotesSidePanel
        open={notesPanel.open}
        mapId={notesPanel.mapId}
        nodeId={notesPanel.nodeId}
        nodeLabel={notesPanel.nodeLabel}
        mapName={mapName}
        onClose={closeNotesPanel}
      />
    </div>
  );
};

/* ─── Main Export (wraps with Provider) ─── */
const MindMapPanel = ({ user }) => (
  <ReactFlowProvider>
    <MindMapInner user={user} />
  </ReactFlowProvider>
);

export default MindMapPanel;
